import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  NOTIFICATION_EMAILS_CONSTANTS,
  TRANSLATION_PROJECTS,
} from '@app/app.constants';
import { GetFollowUpsListAction } from '@app/modules/follow-ups/store/follow-ups.actions';
import { FollowUpsState } from '@app/modules/follow-ups/store/follow-ups.state';
import { ReportsOverviewComponent } from '@app/modules/reports/components/reports/connected-reports-list/reports-overview.component';
import { ReportDetailsDialogComponent } from '@app/modules/reports/components/reports/report-details-dialog/report-details-dialog.component';
import { ReportFormDialogComponent } from '@app/modules/reports/components/reports/report-form-dialog/report-form-dialog.component';
import { ReportViewerDialogComponent } from '@app/modules/reports/components/reports/report-viewer-dialog/report-viewer-dialog.component';
import { UploadReportsFormComponent } from '@app/modules/reports/components/reports/upload-reports-form/upload-reports-form.component';
import {
  ConfirmReportApproval,
  MassApproveReports,
} from '@app/modules/reports/store/report-approval-saga.actions';
import { ReportApprovalSagaState } from '@app/modules/reports/store/report-approval-saga.state';
import {
  ApproveReportAction,
  ArchiveReportAction,
  DisapproveReportAction,
  RefreshReportsAction,
  RemoveReportAction,
} from '@app/modules/reports/store/reports.actions';
import type { ReportsFilter } from '@app/modules/reports/store/reports.state';
import {
  ExtendedReport,
  Report,
  ReportExtensions,
} from '@app/modules/reports/types/report.types';
import { DocumentApprovalSagaConfirmComponent } from '@app/modules/service-data/components/document-approval-saga-confirm/document-approval-saga-confirm.component';
import { ProductInventoryItemUtilsService } from '@app/modules/service-data/services/product-inventory-item-utils.service';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import {
  GetProductInventoryItemAction,
  GetProductInventoryItemListAction,
} from '@app/modules/service-data/store/product-intentory-item.actions';
import { ProductInventoryItemState } from '@app/modules/service-data/store/product-inventory-item.state';
import { SampleStatus } from '@app/modules/service-data/types/sample.types';
import {
  ExportDialogDataType,
  ExportType,
  ExtendedProductInventoryItem,
  ProductInventoryItemStatus,
} from '@app/modules/service-data/types/service-data.types';
import {
  RuntimeStatus,
  TemplateType,
  TranslationProject,
} from '@core/core.types';
import { getTemplateTypeFromReport } from '@core/services/documents-utils';
import { ExportersUtilsService } from '@core/services/exporters-utils.service';
import { NotificationProfilesService } from '@core/services/notification-profiles.service';
import { TranslationProjectService } from '@core/services/translation-project.service';
import { LanguageState } from '@core/store/languages/languages.state';
import { Util } from '@core/utils/core.util';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import {
  ProgressData,
  ProgressDialogComponent,
} from '@shared/components/progress/progress-dialog.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { ConfirmCloseReason } from '@shared/shared.types';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import {
  delay,
  filter,
  finalize,
  first,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { ApproveReportDialogComponent } from '../components/reports/approve-report-dialog/approve-report-dialog.component';
import { ReportSearchResultsComponent } from '../components/reports/report-search-results/report-search-results.component';
import { ReportSearchComponent } from '../components/reports/report-search/report-search.component';

@Injectable({
  providedIn: 'root',
})
export class ReportsUtilsService {
  private modal = inject(NgbModal);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  private confirmService = inject(ConfirmService);
  private piiUtilsService = inject(ProductInventoryItemUtilsService);
  private toastService = inject(ToastService);
  private notificationProfilesService = inject(NotificationProfilesService);
  private translationProjectService = inject(TranslationProjectService);
  private serviceDataUtilsService = inject(ServiceDataUtilsService);
  private exportersUtilsService = inject(ExportersUtilsService);

  // True while an approve/disapprove flow (confirm dialog + request) is
  // running; used by action buttons to prevent double clicks.
  public reportActionInProgress = signal(false);

  viewReport(reportId) {
    const modal = this.modal.open(ReportViewerDialogComponent, {
      modalDialogClass: 'modal-size-xl',
      centered: true,
    });
    modal.componentInstance.reportId.set(reportId);
  }
  openReportsOverview(reportIds: string[]) {
    const modal = this.modal.open(ReportsOverviewComponent, {
      modalDialogClass: 'modal-size-xl',
      centered: true,
    });
    modal.componentInstance.reportIds.set(reportIds);
  }

  uploadReportsDialog() {
    return this.modal.open(UploadReportsFormComponent, {
      backdrop: 'static',
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });
  }

  openReportDetailsDialog(reportId) {
    const modal = this.modal.open(ReportDetailsDialogComponent, {
      modalDialogClass: 'modal-size-xl',
      centered: true,
    });
    modal.componentInstance.reportId.set(reportId);
  }

  openEditReportDialog(reportId) {
    const modal = this.modal.open(ReportFormDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
      backdrop: 'static',
    });
    modal.componentInstance.id.set(reportId);
  }

  openReportSearchDialog() {
    const modal = this.modal.open(ReportSearchComponent, {
      modalDialogClass: 'modal-size-md',
      centered: true,
    });
  }

  openReportSearchResultsDialog(reportIds: string[]) {
    const modal = this.modal.open(ReportSearchResultsComponent, {
      modalDialogClass: 'modal-size-xl',
      centered: true,
    });
    modal.componentInstance.reportIds.set(reportIds);
  }

  public disapproveReport(report: Report) {
    if (this.reportActionInProgress()) {
      return;
    }
    this.reportActionInProgress.set(true);

    this.confirmService
      .confirm({
        title: this.translateService.instant('reports.disapprove.title'),
        message: this.translateService.instant('reports.disapprove.message', {
          fileName: report.id,
        }),
      })
      .pipe(
        switchMap((confirmOpts) => {
          if (confirmOpts.closeReason === ConfirmCloseReason.Yes) {
            return this.store
              .dispatch(new DisapproveReportAction(report.id))
              .pipe(switchMap(() => this.refreshRelatedReports([report])));
          }

          return of(null);
        }),
        finalize(() => this.reportActionInProgress.set(false))
      )
      .subscribe();
  }

  public archiveReports(reports: Report[]) {
    this.confirmService
      .confirm({
        title: this.translateService.instant('reports.archive.title'),
        message: this.translateService.instant('reports.archive.message', {
          number: reports.length,
        }),
      })
      .subscribe((confirmOpts) => {
        if (confirmOpts.closeReason === ConfirmCloseReason.Yes) {
          const progressDialogRef = this.modal.open(ProgressDialogComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            modalDialogClass: 'modal-size-md',
          });
          progressDialogRef.componentInstance.title =
            'reports.progress.archivingReports';

          forkJoin(
            reports.map((report) =>
              this.store.dispatch(new ArchiveReportAction(report.id))
            )
          ).subscribe(() => {
            progressDialogRef.close();
          });
        }
      });
  }

  public removeReport(report: Report) {
    this.confirmService
      .confirm({
        title: this.translateService.instant('reports.remove.title'),
        message: this.translateService.instant('reports.remove.message', {
          fileName: report.id,
        }),
      })
      .pipe(
        switchMap((confirmOpts) => {
          if (confirmOpts.closeReason === ConfirmCloseReason.Yes) {
            return this.store.dispatch(new RemoveReportAction(report.id)).pipe(
              delay(500),
              switchMap(() => this.refreshRelatedReports([report]))
            );
          }
          return of(null);
        })
      )
      .subscribe();
  }

  public approveUnofficialReport(report: Report, onConfirmed?: () => void) {
    if (this.reportActionInProgress()) {
      return;
    }
    this.reportActionInProgress.set(true);

    this.confirmService
      .confirm({
        title: this.translateService.instant(
          'pii.details.approve.documentApprovalTitle'
        ),
        message: this.translateService.instant(
          'pii.details.approve.confirmDocumentApprovalMessage',
          { fileName: report.id }
        ),
      })
      .pipe(
        switchMap((confirmOpts) => {
          if (confirmOpts.closeReason === ConfirmCloseReason.Yes) {
            onConfirmed?.();

            return this.getEmailTemplate(report).pipe(
              delay(500),
              switchMap(() => this.refreshRelatedReports([report]))
            );
          }

          return of(null);
        }),
        finalize(() => this.reportActionInProgress.set(false))
      )
      .subscribe();
  }

  public massApproveReports(
    reports: Report[],
    confirmBeforeApprove = true,
    reloadReportsListTrigger?: WritableSignal<boolean>
  ) {
    const sagaId = Util.CreateGuid();

    const progressSubject = new BehaviorSubject<ProgressData>({
      status: this.translateService.instant(
        'reports.progress.approvingReportsCount',
        { count: reports.length }
      ),
      value: 0,
    });

    const confirmation$ = confirmBeforeApprove
      ? this.confirmService
          .confirm({
            title: this.translateService.instant(
              'pii.details.approve.documentApprovalTitle'
            ),
            message: this.translateService.instant(
              'reports.approve.massApprove.confirmDocumentApprovalMessage'
            ),
          })
          .pipe(first())
      : of({ closeReason: ConfirmCloseReason.Yes });

    confirmation$
      .pipe(
        switchMap((confirmOpts) => {
          if (confirmOpts.closeReason === ConfirmCloseReason.Yes) {
            progressSubject.pipe(delay(1000)).subscribe((progressData) => {
              if (progressData.success) {
                dialogRef.close();
              }
            });

            const dialogRef = this.modal.open(ProgressDialogComponent, {
              centered: true,
              backdrop: 'static',
              keyboard: false,
              modalDialogClass: 'modal-size-md',
            });

            dialogRef.componentInstance.title =
              'reports.progress.approvingReports';
            dialogRef.componentInstance.progressSubject = progressSubject;

            const reportIds = reports.map((report) => report.id);
            return this.store
              .dispatch(new MassApproveReports(reportIds, sagaId))
              .pipe(
                switchMap(() =>
                  this.store.select(
                    ReportApprovalSagaState.isSagaReadyForApproval
                  )
                ),
                first((isSagaReadyForApproval) =>
                  isSagaReadyForApproval(sagaId)
                ),
                switchMap(() =>
                  this.store.select(ReportApprovalSagaState.hasSagaErrors)
                ),
                first(),
                switchMap((hasSagaErrors) => {
                  if (!hasSagaErrors(sagaId)) {
                    return this.store.dispatch(
                      new ConfirmReportApproval(sagaId)
                    );
                  } else {
                    return this.store
                      .select(ReportApprovalSagaState.getSagaById)
                      .pipe(
                        first(),
                        switchMap((getSagaById) => {
                          const modalRef = this.modal.open(
                            DocumentApprovalSagaConfirmComponent,
                            {
                              centered: true,
                            }
                          );

                          modalRef.componentInstance.state =
                            getSagaById(sagaId).state;

                          return modalRef.closed.pipe(
                            switchMap((result) => {
                              if (result) {
                                return this.store
                                  .dispatch(new ConfirmReportApproval(sagaId))
                                  .pipe(map(() => result));
                              } else {
                                return of(null);
                              }
                            })
                          );
                        })
                      );
                  }
                }),
                switchMap((result) => {
                  if (!result) {
                    return of(null);
                  }

                  return this.store
                    .select(ReportApprovalSagaState.isSagaInProgress)
                    .pipe(
                      first((isSagaInProgress) => !isSagaInProgress(sagaId)),
                      switchMap(() => this.refreshRelatedReports(reports)),
                      map(() => result)
                    );
                })
              );
          }
        })
      )
      .subscribe((result) => {
        progressSubject.next({ status: 'Done', value: 100, success: true });

        if (result) {
          reloadReportsListTrigger?.update((value) => !value);
        }
      });
  }

  public exportSelectedReports(
    reportIds: string[],
    exportType = ExportType.DigitalReports
  ): Observable<string> {
    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translateService.instant(
        'reports.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      )
    );

    return this.exportersUtilsService
      .exportSelected(ExportDialogDataType.Reports, reportIds, exportType)
      .pipe(
        map((status: string) => {
          if (status !== RuntimeStatus.Running) {
            progressSubject.next({
              value: 100,
              status: 'shared.progress.done',
              success: status === RuntimeStatus.Completed ? true : false,
            });
          }

          return status;
        })
      );
  }

  public exportReports(
    filters?: {
      [key: string]: string;
    },
    exportType = ExportType.DigitalReports
  ): Observable<string> {
    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translateService.instant(
        'reports.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      )
    );

    return this.exportersUtilsService.export(filters ?? {}, exportType).pipe(
      map((status: string) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }

        return status;
      })
    );
  }

  buildHttpParams(params: ReportsFilter) {
    let httpParams = HttpUtils.buildHttpParams(params);

    if (params.ids?.length) {
      httpParams = httpParams.set('ids', params.ids.join(','));
    }

    if (params.rootIds?.length) {
      httpParams = httpParams.set('rootIds', params.rootIds.join(','));
    }

    if (params.kitIds?.length) {
      httpParams = httpParams.set('kitIds', params.kitIds.join(','));
    }

    if (params.lastStatuses?.length) {
      httpParams = httpParams.set(
        'lastStatuses',
        params.lastStatuses.join(',')
      );
    }

    if (params.countries?.length) {
      httpParams = httpParams.set('countries', params.countries.join(','));
    }

    if (params.laboratories?.length) {
      httpParams = httpParams.set(
        'laboratories',
        params.laboratories.join(',')
      );
    }

    if (params.productIds?.length) {
      httpParams = httpParams.set('productIds', params.productIds.join(','));
    }

    if (params.partnerIds?.length) {
      httpParams = httpParams.set('partnerIds', params.partnerIds.join(','));
    }

    return httpParams;
  }

  // Paternity and NIPPT have a report per participant, linked through
  // `relatedReportIds`. Approving or removing one report changes all of them,
  // so the related reports and their product inventory items are reloaded too.
  private refreshRelatedReports(reports: Report[]): Observable<void> {
    const reportIds = [
      ...new Set(
        reports.flatMap((report) => [
          report.id,
          ...(report.relatedReportIds ?? []),
        ])
      ),
    ];
    // Related reports may not be loaded yet, their items can still be known
    const getPiiByReportId = this.store.selectSnapshot(
      ProductInventoryItemState.getProductInventoryItemByReportId
    );
    const productInventoryItemIds = [
      ...reports.map((report) => report.productInventoryItemId),
      ...reportIds
        .map((reportId) => getPiiByReportId(reportId)?.id)
        .filter((id) => !!id),
    ];

    return this.store.dispatch(
      new RefreshReportsAction(reportIds, productInventoryItemIds)
    );
  }

  private getEmailTemplate(report: Report): Observable<{
    serviceName: string;
    predefinedRecipients: {
      [country: string]: { cc: string[]; bcc: string[] };
    };
    subject: string;
    body: string;
  }> {
    return this.store
      .dispatch(
        new GetProductInventoryItemAction(
          report.productInventoryItemId,
          true,
          true
        )
      )
      .pipe(
        switchMap(() =>
          this.store
            .select(ProductInventoryItemState.getProductInventoryItemById)
            .pipe(
              take(1),
              map((getPiiById) => getPiiById(report.productInventoryItemId))
            )
        ),
        switchMap((pii) =>
          this.piiUtilsService.getExtendedProductInventoryItem(pii, {
            sample: { serviceData: { invoice: true }, kit: {} },
            product: true,
            partner: true,
          })
        ),
        take(1),
        switchMap((pii) => {
          if (
            pii.lastStatus === ProductInventoryItemStatus.processingFailed ||
            pii.lastStatus === ProductInventoryItemStatus.canceled
          ) {
            this.toastService.warning(
              this.translateService.instant(
                'pii.details.approve.approvalWarningMessage',
                {
                  kitId: pii.kitId,
                  lastStatus: pii.lastStatus,
                }
              ),
              this.translateService.instant(
                'pii.details.approve.approvalWarningTitle'
              ),
              0
            );

            return of(null);
          } else if (
            pii.sample.status === SampleStatus.redrawn ||
            pii.sample.status === SampleStatus.forResample
          ) {
            this.toastService.warning(
              this.translateService.instant(
                'pii.details.approve.approvalSampleWarningMessage',
                {
                  lastStatus: pii.sample.status,
                }
              ),
              this.translateService.instant(
                'pii.details.approve.approvalWarningTitle'
              ),
              0
            );

            return of(null);
          }

          const templateType = getTemplateTypeFromReport(
            report,
            pii.sample.serviceData
          );

          return this.notificationProfilesService
            .getNotificationProfilesOfPartner(
              this.serviceDataUtilsService.getPropertyFromServiceInformation(
                pii.sample.serviceData.serviceInformation,
                'doctorInformation.doctorId'
              )
            )
            .pipe(
              first(),
              switchMap((notificationProfiles) => {
                const notificationProfile =
                  notificationProfiles && notificationProfiles.length
                    ? notificationProfiles.find(
                        (np) => np.name === templateType
                      )
                    : null;

                return this.retrieveMailTemplate(pii, templateType).pipe(
                  switchMap((emailTemplate) => {
                    const modalRef = this.modal.open(
                      ApproveReportDialogComponent,
                      {
                        centered: true,
                        modalDialogClass: 'modal-size-lg',
                      }
                    );

                    modalRef.componentInstance.report.set(report);
                    modalRef.componentInstance.kitId.set(pii.kitId);
                    modalRef.componentInstance.templateType.set(templateType);
                    modalRef.componentInstance.notificationProfile.set(
                      notificationProfile
                    );
                    modalRef.componentInstance.emailTemplate.set(emailTemplate);

                    return modalRef.closed.pipe(
                      switchMap((data) => {
                        if (data) {
                          return this.store
                            .dispatch(new ApproveReportAction(report.id, data))
                            .pipe(map(() => data));
                        } else {
                          return of(null);
                        }
                      })
                    );
                  })
                );
              })
            );
        })
      );
  }

  public getExtendedReports(
    reports: Report[],
    appends: ReportExtensions = {
      pii: null,
      followUps: true,
    }
  ): Observable<ExtendedReport[]> {
    const reportIds = reports.map((report) => report.id);
    if (reportIds.length === 0) return of([]);
    return forkJoin([
      this.store.dispatch(
        new GetProductInventoryItemListAction({ reportIds }, null, true)
      ),
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction(
              {
                ids: reports.map((report) => report.followUpIds).flat(),
              },
              {},
              null,
              null
            )
          )
        : of(null),
    ]).pipe(
      switchMap(() =>
        this.store
          .select(ProductInventoryItemState.getProductInventoryItemsByReportIds)
          .pipe(map((getByReportIds) => getByReportIds(reportIds)))
          .pipe(
            switchMap((piis) =>
              this.piiUtilsService.loadProductInventoryItemConnectedData(
                piis,
                appends.pii
              )
            )
          )
      ),
      switchMap(() =>
        combineLatest(
          reports.map((report) =>
            combineLatest([
              of(report),
              appends.pii
                ? this.store
                    .select(
                      ProductInventoryItemState.getProductInventoryItemByReportId
                    )
                    .pipe(
                      map((getById) => getById(report.id)),
                      switchMap((pii) =>
                        this.piiUtilsService.getExtendedProductInventoryItem(
                          pii,
                          appends.pii
                        )
                      )
                    )
                : of(null),
              appends.followUps
                ? this.store
                    .select(FollowUpsState.getFollowUpsByIds)
                    .pipe(
                      map((getFollowUpsByIds) =>
                        getFollowUpsByIds(report?.followUpIds)
                      )
                    )
                : of(null),
            ])
          )
        )
      ),
      map((items) =>
        items.map(([report, productInventoryItem, followUps]) => ({
          ...report,
          productInventoryItem,
          followUps,
        }))
      )
    );
  }

  private retrieveMailTemplate(
    pii: ExtendedProductInventoryItem,
    templateType: TemplateType
  ): Observable<{
    serviceName: string;
    predefinedRecipients: {
      [country: string]: { cc: string[]; bcc: string[] };
    };
    subject: string;
    body: string;
  }> {
    return this.loadTranslationProject(templateType).pipe(
      first(),
      filter((templateType) => !!templateType),
      map((translationProject) => {
        const response = {
          serviceName: pii.product.brand,
          predefinedRecipients: {},
          subject: '',
          body: '',
        };

        if (pii.partner && pii.partner.address && pii.partner.address.country) {
          const partnerCountryKey = pii.partner.address.country
            .toLowerCase()
            .split(' ')
            .join('');
          response.predefinedRecipients =
            this.notificationProfilesService.recipientsByCountry[
              partnerCountryKey
            ];
          if (!response.predefinedRecipients) {
            response.predefinedRecipients = this.notificationProfilesService
              .recipientsByCountry.default ?? { cc: [], bcc: [] };
          }
        }

        if (translationProject && pii.partner) {
          const language =
            pii.partner.languageIds && pii.partner.languageIds.length
              ? this.store.selectSnapshot(LanguageState.getLanguageById)(
                  pii.partner.languageIds[0]
                )?.name
              : translationProject.languages[0];
          const subjectTerm = translationProject.translationTerms.find(
            (t) => t.term === NOTIFICATION_EMAILS_CONSTANTS.subjectTerm
          );
          const bodyTerm = translationProject.translationTerms.find(
            (t) => t.term === NOTIFICATION_EMAILS_CONSTANTS.bodyTerm
          );

          if (subjectTerm) {
            const subjectTranslation =
              subjectTerm.translations.find((t) => t.language === language) ||
              subjectTerm.translations.find(
                ({ language: tLanguage }) =>
                  tLanguage.toLowerCase() === 'english'
              );
            response.subject = subjectTranslation.translation;
          }

          if (bodyTerm) {
            const bodyTranslation =
              bodyTerm.translations.find((t) => t.language === language) ||
              bodyTerm.translations.find(
                ({ language: tLanguage }) =>
                  tLanguage.toLowerCase() === 'english'
              );
            response.body = bodyTranslation.translation;
          }

          return response;
        }
      })
    );
  }

  private loadTranslationProject(
    templateType: string
  ): Observable<TranslationProject> {
    const key = templateType.toLowerCase().split(' ').join('');

    if (TRANSLATION_PROJECTS[key]) {
      return this.translationProjectService.get(TRANSLATION_PROJECTS[key]);
    }

    return of(null);
  }
}
